// emailHtml.js — turn a plain-text transactional email body into a branded
// HTML part, rendered AT SEND TIME.
// ===========================================================================
// Why this exists: balance requests went out as text/plain only, so the pay
// link was a bare URL on its own line. Most clients auto-linkify that, but it
// reads like a raw paste and converts worse than a real button — and the
// strict plain-text readers don't linkify at all, leaving the customer to
// copy/paste a URL to pay us. (Ryder, 2026-09-12.)
//
// 🚨 RENDERED AT SEND, NOT AT DRAFT. The owner edits draft bodies by hand in
// the Outbox (Dashboard's draft editor). HTML stored on the email_drafts row
// would silently keep the ORIGINAL wording — so an edited amount would show
// the old figure in the HTML part and the new one in the text part, which is
// exactly the kind of mismatch that turns into a payment dispute. Deriving
// everything from the final text means the two parts can never disagree.
//
// The text part still ships as the fallback (see api/send-email.js), so a
// client that refuses HTML loses nothing it had before this file existed.
// ===========================================================================

// Brand tokens — matched to the gift-card email in api/invoicer.js so the two
// transactional emails look like they came from the same company.
const BLUE = '#2563eb';
const INK = '#374151';
const MUTED = '#6b7280';
const FONT = "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif";

const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ESC[c]); }

// Trailing punctuation that reads as sentence-ending, not part of the URL.
function trimUrlTail(u) { return u.replace(/[.,;:!?)\]]+$/, ''); }

// Linkify + tel-ify an ALREADY-ESCAPED string. Escaping first means a '&' in a
// query string is '&amp;' by the time we get here, which is the correct href
// form anyway — so the same token serves as both href and visible label.
function inlineLinks(escaped) {
  let out = escaped.replace(/https?:\/\/[^\s<]+/g, (m) => {
    const url = trimUrlTail(m);
    return '<a href="' + url + '" style="color:' + BLUE + ';text-decoration:underline;">' + url + '</a>'
      + m.slice(url.length);
  });
  out = out.replace(/\b(\d{3})-(\d{3})-(\d{4})\b/g,
    (m, a, b, c) => '<a href="tel:+1' + a + b + c + '" style="color:' + BLUE + ';text-decoration:none;">' + m + '</a>');
  return out;
}

const isUrlOnly = (line) => /^https?:\/\/\S+$/.test(line.trim());
// A shouted section label like "PAY ONLINE — $348.72": no lowercase letters,
// at least a few capitals, short enough to be a heading rather than a shouted
// paragraph. "— 3C Detailing" has lowercase, so the sign-off never matches.
const isHeading = (line) => {
  const t = line.trim();
  return t.length > 0 && t.length <= 60 && !/[a-z]/.test(t) && (t.match(/[A-Z]/g) || []).length >= 3;
};

// What a pay button should say. Derived from the TEXT, never from the booking
// row, so an owner who edits the amount in the draft gets a button that agrees
// with the sentence above it.
//
// Two shapes to serve: Colonie's email headings each total ("PAY ONLINE — $x",
// "PAY BY CHECK — $y"), so a button takes the heading directly above it;
// Kingston's states one balance up top and has no headings, so it falls back
// to the first amount in the body. Both land on the right number; anything we
// can't read degrades to a generic label rather than guessing wrong.
function buttonLabel(url, precedingHeading, firstAmount) {
  const money = (precedingHeading && (precedingHeading.match(/\$[\d,]+(?:\.\d{2})?/) || [])[0]) || firstAmount || '';
  if (/method=check/.test(url)) return 'Tell us you’re paying by check';
  if (/3c-balance\.html/.test(url)) return money ? 'Pay ' + money : 'Pay your balance';
  if (/3c-deposit\.html/.test(url)) return money ? 'Pay ' + money + ' deposit' : 'Pay your deposit';
  if (/3c-vip\.html/.test(url)) return 'Open your portal';
  if (/3c-unsubscribe\.html/.test(url)) return 'Unsubscribe';
  return 'Open the link';
}

// Bulletproof-ish button: a table cell carries the background so Outlook fills
// it, and the <a> carries the padding so the whole block is clickable.
function button(url, label, primary) {
  const bg = primary ? BLUE : '#ffffff';
  const fg = primary ? '#ffffff' : BLUE;
  const border = primary ? BLUE : '#c7d2fe';
  return '<table role="presentation" cellpadding="0" cellspacing="0" style="margin:6px 0 4px;">'
    + '<tr><td align="center" bgcolor="' + bg + '" style="border-radius:8px;border:1px solid ' + border + ';">'
    + '<a href="' + url + '" style="display:inline-block;padding:' + (primary ? '14px 30px' : '11px 24px')
    + ';font-family:' + FONT + ';font-size:' + (primary ? '16px' : '14px') + ';font-weight:700;color:' + fg
    + ';text-decoration:none;border-radius:8px;">' + esc(label) + '</a>'
    + '</td></tr></table>';
}

/**
 * Render a plain-text email body as a branded HTML document.
 * @param {string} text  the final body, exactly as it will ship as text/plain
 * @returns {string} full HTML document, or '' when there is nothing to render
 */
export function renderEmailHtml(text) {
  const raw = String(text || '');
  if (!raw.trim()) return '';
  const lines = raw.replace(/\r\n?/g, '\n').split('\n');

  // Split the sign-off ("— 3C Detailing" + any address lines) off the end so it
  // renders as a footer bar instead of a stray paragraph.
  let cut = -1;
  for (let i = lines.length - 1; i >= 0; i--) {
    if (/^\s*[—-]\s*3C\b/.test(lines[i])) { cut = i; break; }
  }
  const bodyLines = cut >= 0 ? lines.slice(0, cut) : lines;
  const signOff = cut >= 0 ? lines.slice(cut).filter((l) => l.trim()) : [];

  const firstAmount = (raw.match(/\$[\d,]+(?:\.\d{2})?/) || [])[0] || '';
  const blocks = [];
  let para = [];       // lines of the paragraph being accumulated
  let lastHeading = '';
  let usedPrimary = false;

  const flush = () => {
    if (!para.length) return;
    blocks.push('<p style="margin:0 0 14px;color:' + INK + ';font-size:15px;line-height:1.6;">'
      + inlineLinks(esc(para.join('\n'))).replace(/\n/g, '<br>') + '</p>');
    para = [];
  };

  for (const line of bodyLines) {
    if (!line.trim()) { flush(); continue; }
    if (isUrlOnly(line)) {
      flush();
      const url = esc(trimUrlTail(line.trim()));
      const primary = !usedPrimary;
      blocks.push(button(url, buttonLabel(url, lastHeading, firstAmount), primary));
      // The copy/paste escape hatch, primary button only: a customer whose
      // client strips the button still has to be able to pay us.
      if (primary) {
        blocks.push('<p style="margin:0 0 16px;color:#9ca3af;font-size:11px;line-height:1.5;word-break:break-all;">'
          + 'Button not working? Paste this into your browser:<br>' + url + '</p>');
      }
      usedPrimary = true;
      continue;
    }
    if (isHeading(line)) {
      flush();
      lastHeading = line.trim();
      blocks.push('<div style="margin:18px 0 6px;color:#111827;font-size:13px;font-weight:700;'
        + 'letter-spacing:0.08em;text-transform:uppercase;">' + inlineLinks(esc(lastHeading)) + '</div>');
      continue;
    }
    para.push(line);
  }
  flush();

  const footer = signOff.length
    ? '<tr><td style="padding:14px 28px;background:#f9fafb;border-top:1px solid #eceff3;color:#9ca3af;'
      + 'font-size:11px;line-height:1.6;">' + inlineLinks(esc(signOff.join('\n'))).replace(/\n/g, '<br>') + '</td></tr>'
    : '';

  return '<!doctype html><html><body style="margin:0;padding:0;background:#eef1f6;font-family:' + FONT + ';">'
    + '<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#eef1f6;padding:24px 12px;">'
    + '<tr><td align="center">'
    + '<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:560px;background:#ffffff;'
    + 'border-radius:14px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,0.06);">'
    + '<tr><td style="padding:26px 28px 2px;"><div style="font-size:13px;letter-spacing:0.14em;text-transform:uppercase;'
    + 'color:' + BLUE + ';font-weight:700;">3C Detailing</div></td></tr>'
    + '<tr><td style="padding:16px 28px 22px;font-family:' + FONT + ';color:' + MUTED + ';">'
    + blocks.join('\n') + '</td></tr>'
    + footer
    + '</table></td></tr></table></body></html>';
}
